import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { decodePolyline, type LatLng } from '@tcrd/shared';
import { cn } from '@tcrd/ui';
import { env } from '../../lib/env';
import { isChunkLoadError, reloadOnStaleChunk } from '../../lib/pwa';
import type { MapEngine, MarkerSpec } from './engine';

export interface MapViewProps {
  markers?: MarkerSpec[];
  /** Polilínea codificada o lista de puntos. */
  route?: string | LatLng[] | null;
  /** Puntos a encuadrar; si se omite, se encuadran marcadores + ruta. */
  fitTo?: LatLng[];
  onMapClick?: (p: LatLng) => void;
  interactive?: boolean;
  className?: string;
  /** Cambiar esta clave fuerza un nuevo encuadre. */
  fitKey?: string;
  padding?: number;
  /** Notifica cuando el motor está listo (para animaciones como el GPS). */
  onEngine?: (e: MapEngine | null) => void;
}

/**
 * Mapa con motor intercambiable: MapLibre + OpenFreeMap (gratuito, por
 * defecto) o Google Maps si se configura una clave. Expone el motor por ref para animaciones (GPS).
 */
export const MapView = forwardRef<MapEngine | null, MapViewProps>(function MapView(
  { markers = [], route, fitTo, onMapClick, interactive = true, className, fitKey, padding, onEngine },
  ref,
) {
  const el = useRef<HTMLDivElement>(null);
  const [engine, setEngine] = useState<MapEngine | null>(null);
  const [error, setError] = useState<string | null>(null);
  useImperativeHandle(ref, () => engine as MapEngine, [engine]);
  const onEngineRef = useRef(onEngine);
  onEngineRef.current = onEngine;
  useEffect(() => onEngineRef.current?.(engine), [engine]);

  useEffect(() => {
    if (!el.current) return;
    let disposed = false;
    let e: MapEngine | null = null;
    (async () => {
      try {
        if (env.googleMapsKey) {
          const g = await import('./google-engine');
          await g.loadGoogleMaps();
          if (disposed || !el.current) return;
          e = g.createGoogleEngine(el.current, { interactive });
        } else {
          // MapLibre (~1 MB) se carga solo en pantallas con mapa.
          const m = await import('./maplibre-engine');
          if (disposed || !el.current) return;
          e = m.createMapLibreEngine(el.current, { interactive });
        }
        setEngine(e);
      } catch (err) {
        if (isChunkLoadError(err) && reloadOnStaleChunk()) return;
        setError((err as Error).message);
      }
    })();
    return () => {
      disposed = true;
      e?.destroy();
      setEngine(null);
    };
  }, [interactive]);

  const points = useMemo(() => (typeof route === 'string' ? decodePolyline(route) : route ?? null), [route]);

  useEffect(() => engine?.setMarkers(markers), [engine, markers]);
  useEffect(() => engine?.setRoute(points), [engine, points]);
  useEffect(() => engine?.onClick(onMapClick ?? null), [engine, onMapClick]);

  const fitPoints = useMemo(() => fitTo ?? [...markers.map((m) => m.position), ...(points ?? [])], [fitTo, markers, points]);
  const fitSignature = fitKey ?? fitPoints.map((p) => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`).join('|');
  useEffect(() => {
    if (engine && fitPoints.length) engine.fit(fitPoints, padding);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine, fitSignature]);

  return (
    // 'relative' solo si la pantalla no lo posiciona: con 'relative' y 'absolute' juntos gana 'relative' en Tailwind 4
    // y el mapa de pantalla completa quedaba con 0 px de alto.
    <div className={cn(/\b(absolute|fixed)\b/.test(className ?? '') ? null : 'relative', 'overflow-hidden bg-brand-gray dark:bg-white/5', className)}>
      <div ref={el} className="absolute inset-0" role="application" aria-label="Mapa" />
      {error && <p className="absolute inset-x-0 bottom-2 mx-auto w-fit rounded-xl bg-black/70 text-white text-xs px-3 py-1.5">{error}</p>}
    </div>
  );
});
